import { META_UNILEVER_COLUMNS, MetaUnileverRow, SkippedRow, ValidationError } from './types';

export const EXPECTED_HEADERS = META_UNILEVER_COLUMNS.map((column) => column.label);

export function makeLocalGuid() {
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createEmptyRow(): MetaUnileverRow {
  return {
    guid: makeLocalGuid(),
    distribuidor: '',
    consultor: '',
    coordenador: '',
    executivoBpc: '',
    executivoHf: '',
    regional: '',
    metaHpf: '',
  };
}

export function normalizeText(value: any) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function normalizeKey(value: any) {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function formatMeta(value: any) {
  if (value === null || value === undefined || value === '') return '';
  const numberValue = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(numberValue)) return String(value);
  return numberValue.toFixed(2);
}

export function parseMetaNumber(value: any) {
  const formatted = formatMeta(value);
  if (formatted === '') return null;
  const numberValue = Number(formatted);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function formatCurrencyBRL(value: any) {
  const numberValue = parseMetaNumber(value);
  if (numberValue === null) return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue);
}

export function sanitizeMetaInput(value: string) {
  return value.replace(',', '.').replace(/[^\d.-]/g, '');
}

export function finalizeMetaValue(value: any) {
  const formatted = formatMeta(value);
  return formatted === '' ? '' : formatted;
}

export function prepareRowsForSave(rows: MetaUnileverRow[]) {
  return rows
    .map((row) => ({
      ...row,
      distribuidor: normalizeText(row.distribuidor),
      consultor: normalizeText(row.consultor),
      coordenador: normalizeText(row.coordenador),
      executivoBpc: normalizeText(row.executivoBpc),
      executivoHf: normalizeText(row.executivoHf),
      regional: normalizeText(row.regional),
      metaHpf: formatMeta(row.metaHpf) || null,
    }))
    .filter((row) => !isBlankRow(row));
}

export function isBlankRow(row: MetaUnileverRow) {
  return META_UNILEVER_COLUMNS.every((column) => {
    const value = row[column.key];
    return value === null || value === undefined || String(value).trim() === '';
  });
}

export function rowsAreEqual(a: MetaUnileverRow, b: MetaUnileverRow) {
  return META_UNILEVER_COLUMNS.every((column) => {
    const left = column.type === 'money' ? formatMeta(a[column.key]) : normalizeText(a[column.key]);
    const right = column.type === 'money' ? formatMeta(b[column.key]) : normalizeText(b[column.key]);
    return left === right;
  });
}

export function getChangedRows(rows: MetaUnileverRow[], originalRows: MetaUnileverRow[]) {
  const originalByGuid = new Map(originalRows.map((row) => [row.guid, row]));

  return rows.filter((row) => {
    if (isBlankRow(row)) return false;
    const original = originalByGuid.get(row.guid);
    return !original || !rowsAreEqual(row, original);
  });
}

export function mergeRowsByDistributor(
  currentRows: MetaUnileverRow[],
  importedRows: MetaUnileverRow[]
) {
  const nextRows = [...currentRows];
  const indexByDistributor = new Map<string, number>();

  nextRows.forEach((row, index) => {
    if (row.distribuidor) {
      indexByDistributor.set(normalizeKey(row.distribuidor), index);
    }
  });

  importedRows.forEach((row) => {
    const key = normalizeKey(row.distribuidor);
    const index = indexByDistributor.get(key);

    if (index === undefined) {
      nextRows.push(row);
      indexByDistributor.set(key, nextRows.length - 1);
    } else {
      nextRows[index] = {
        ...row,
        guid: nextRows[index].guid,
      };
    }
  });

  return nextRows;
}

export function validationErrorLines(errors: ValidationError[] = []) {
  return errors.map((error) => {
    const row = error.row > 0 ? `Linha ${error.row}` : 'Tabela';
    return `${row}: ${error.message}`;
  });
}

export function skippedRowLines(rows: SkippedRow[] = []) {
  return rows.map((row) => {
    const reasons = (row.reasons || []).map((reason) => reason.message).join(' | ');
    return `Linha ${row.row}: ${row.distribuidor || '-'} / ${row.regional || '-'}${reasons ? ` - ${reasons}` : ''}`;
  });
}
